import { Inject } from '@odinjs/odin';
import { ButtonAPI, FieldListAPI, FormAPI, ModalAPI, PanelAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { SubmitEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, SendMailVaccineAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import { checkValidEmailList } from '../../utils/user-util';
import BaseController from '../BaseController';

interface ContextData {
  data: MailParams;
}

interface MailParams {
  contents: Record<string, Value>,
  groupCode: number | string,
}

@Controller({ fragment: FragmentNames.SEND_MAIL_VACCINE })
class MyChartDischargeVaccineEmailController extends BaseController {

  @Inject(SendMailVaccineAliases.PANEL_LOADER_SEND_MAIL)
    panelLoaderSendMailApi!: PanelAPI;

  @Inject(SendMailVaccineAliases.SEND_MAIL_PANEL)
    sendMailPanelApi!: PanelAPI;

  @Inject(SendMailVaccineAliases.SEND_MAIL_FIELD_NOTES)
    sendMailFieldNotesApi!: TextAreaAPI;

  @Inject(SendMailVaccineAliases.FIELD_LIST_DROPDOWN_SEND_MAIL)
    fieldListDropdownSendMailApi!: FieldListAPI;

  @Inject(SendMailVaccineAliases.SEND_MAIL_VACCINE)
    mainModalApi!: ModalAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject(SendMailVaccineAliases.BUTTON_SEND_MAIL )
    buttonSendMailAPI!: ButtonAPI;

  @Inject(SendMailVaccineAliases.FORM_DROPDOWN_SEND_MAIL)
    formDropdownSendMailAPI!: FormAPI;

  @OnValidate({ target: SendMailVaccineAliases.FORM_DROPDOWN_SEND_MAIL })
  handleFormDropdownSendMailValidate(event: ValidateEvent): void {
    this.buttonSendMailAPI.setEnabled(event.valid);
  }

  @OnClick({ target: SendMailVaccineAliases.BUTTON_SEND_MAIL })
  onConfirmSendMailClick(): void {
    interface EmailFromFieldList {
      email: string;
    }

    if (checkValidEmailList(this.fieldListDropdownSendMailApi.getValue())) {
      const { patientId } = UserContext;
      const mailList = this.fieldListDropdownSendMailApi.getValue() as unknown as EmailFromFieldList[];
      const context = this.context as unknown as ContextData;
      const mailParams = context.data;
      const body = {
        contents: mailParams.contents,
        groupCode: mailParams.groupCode,
        notes: this.sendMailFieldNotesApi.getValue(),
        receivers: mailList.map(item => { return item.email}),
      };

      this.setLoaderSendMailDropDown(true);
      HttpService.post(APPLICATION_ENDPOINTS.POST_MC_VACCINE_EMAIL, { patientId }, { body })
        .then(() => {
          this.submittableFragmentContext.submit(new SubmitEvent());
          showToastNotificationSuccess('patient.email-sended');
        })
        .catch(() => {
          showToastNotificationError('newaccount.backend.error.sending.email');
          this.setLoaderSendMailDropDown(false);
        });
    }
  }

  setLoaderSendMailDropDown(showLoader: boolean): void {
    if (showLoader) {
      this.context.set({ formDropdownSendEmailData: this.formDropdownSendMailAPI.getRecord() })
    }

    this.sendMailPanelApi.setVisible(!showLoader);
    this.panelLoaderSendMailApi.setVisible(showLoader);

    if (!showLoader) {
      const previousFormData = this.context.get('formDropdownSendEmailData') as Record<string, Value>;
      if (previousFormData) {
        this.formDropdownSendMailAPI.setData(previousFormData);
      }
    }
  }
}

export default MyChartDischargeVaccineEmailController;